import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { AppDispatch, RootState } from "../../../redux/store";
import { listenToCheckoutSession } from "../../../redux/paymentsSlice";
import { ALL_CURRENCIES, CurrencyItem } from "../../../GLOBAL/Currencies";
import logo from "../../../assets/cosmoola/logo.jpg";
import {
  ArrowLeft, CreditCard, Smartphone, Shield, Lock,
  ChevronDown, AlertCircle, Copy, Check, RefreshCw,
} from "lucide-react";

/* ══ THEME — exact PaymentMethods ══ */
const D={bg:"#000000",surface:"#0a0a0a",surface2:"#101010",border:"#1c1c1c",border2:"#2a2a2a",white:"#ffffff",offwhite:"#e8e8e8",muted:"#6b6b6b",gold:"#C9A96E"};
const L={bg:"#ffffff",surface:"#fafafa",surface2:"#f3f3f3",border:"#e5e5e5",border2:"#d4d4d4",text:"#0a0a0a",textSoft:"#525252",muted:"#a3a3a3"};

const API="https://cosmoola.com/api";

type PayTab="cospay"|"card"|"mobile"|"google"|"apple";
type Stage="form"|"pending"|"success"|"error";
type MobileKey="mpesa"|"momo"|"airtel"|"orange";

interface Session {
  id:string;status:string;payment_intent:string;client_secret:string;
  amount_total:number;currency:string;success_url:string;cancel_url:string;
  expires_at:number;mobile_config?:{method?:string}|null;
  payment_method_types:string[];metadata?:Record<string,string>;
  line_items?:{data:{price_data?:{unit_amount:number;currency:string;product_data?:{name?:string}};quantity?:number}[]};
}

/* FX */
const FX:Record<string,number>={EUR:1,USD:0.92,GBP:1.17,CHF:1.13,CAD:0.68,AUD:0.59,JPY:0.0062,KES:0.0068,GHS:0.058,UGX:0.00025,XOF:0.0015,NGN:0.00056,ZAR:0.049,INR:0.011,AED:0.25};
const fromEUR=(eur:number,code:string)=>eur/(FX[code.toUpperCase()]??1);
const fmtEUR=(n:number)=>"€"+n.toLocaleString("en",{minimumFractionDigits:2,maximumFractionDigits:2});
const fmtCur=(n:number,item:CurrencyItem)=>item.symbol+n.toLocaleString("en",{minimumFractionDigits:2,maximumFractionDigits:2});
const fmtCard=(v:string)=>v.replace(/\D/g,"").slice(0,16).replace(/(.{4})/g,"$1 ").trim();
const fmtExp=(v:string)=>{const d=v.replace(/\D/g,"").slice(0,4);return d.length>2?d.slice(0,2)+"/"+d.slice(2):d;};

async function apiPost(ep:string,body:Record<string,unknown>){const r=await fetch(`${API}/${ep}`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(body)});return r.json();}
async function apiGet(ep:string,p:Record<string,string>={}){const r=await fetch(`${API}/${ep}?${new URLSearchParams(p)}`);return r.json();}

const MOBILE_PROVIDERS=[
  {key:"mpesa" as MobileKey,label:"M-Pesa",flag:"🇰🇪",hint:"e.g. 0712 345 678",ep:"initiateMpesa",cur:"KES",sym:"KSh"},
  {key:"momo"  as MobileKey,label:"MTN MoMo",flag:"🇬🇭",hint:"e.g. 024 123 4567",ep:"initiateMtnMomo",cur:"GHS",sym:"GH₵"},
  {key:"airtel"as MobileKey,label:"Airtel Money",flag:"🇺🇬",hint:"e.g. 075 123 4567",ep:"initiateAirtel",cur:"UGX",sym:"USh"},
  {key:"orange"as MobileKey,label:"Orange Money",flag:"🇸🇳",hint:"e.g. 77 123 45 67",ep:"initiateOrange",cur:"XOF",sym:"CFA"},
];

/* ── Currency Picker ── */
function CurrencyPicker({selected,onSelect}:{selected:CurrencyItem;onSelect:(c:CurrencyItem)=>void}){
  const [open,setOpen]=useState(false);const [q,setQ]=useState("");const ref=useRef<HTMLDivElement>(null);
  useEffect(()=>{const h=(e:MouseEvent)=>{if(ref.current&&!ref.current.contains(e.target as Node))setOpen(false);};document.addEventListener("mousedown",h);return()=>document.removeEventListener("mousedown",h);},[]);
  const filtered=ALL_CURRENCIES.filter(c=>!q||c.name.toLowerCase().includes(q.toLowerCase())||c.code.toLowerCase().includes(q.toLowerCase()));
  return(
    <div ref={ref} style={{position:"relative"}}>
      <button onClick={()=>setOpen(v=>!v)} style={{display:"flex",alignItems:"center",gap:6,padding:"7px 12px",background:"transparent",border:`1px solid ${D.border2}`,cursor:"pointer",fontSize:11,fontFamily:"'Montserrat',sans-serif",color:D.offwhite,letterSpacing:"0.05em"}}>
        <span>{selected.flag}</span><span style={{fontWeight:600}}>{selected.code}</span>
        <ChevronDown size={10} color={D.muted} style={{transform:open?"rotate(180deg)":"none",transition:"transform 0.2s"}}/>
      </button>
      {open&&(
        <div style={{position:"absolute",top:"calc(100% + 4px)",right:0,background:L.bg,border:`1px solid ${L.border}`,zIndex:300,width:210,boxShadow:"0 8px 32px rgba(0,0,0,0.18)"}}>
          <div style={{padding:"8px"}}><input autoFocus placeholder="Search…" value={q} onChange={e=>setQ(e.target.value)} style={{width:"100%",padding:"8px 10px",border:`1px solid ${L.border}`,fontSize:11,fontFamily:"'Montserrat',sans-serif",outline:"none",color:L.text,background:L.surface2,boxSizing:"border-box" as const}}/></div>
          <div style={{maxHeight:240,overflowY:"auto"}}>
            {filtered.map(c=>(
              <div key={c.code} onClick={()=>{onSelect(c);setOpen(false);setQ("");}} style={{display:"flex",alignItems:"center",gap:8,padding:"9px 12px",cursor:"pointer",fontSize:12,color:L.text,background:selected.code===c.code?L.surface:"transparent",borderBottom:`1px solid ${L.border}`}}
                onMouseEnter={e=>(e.currentTarget.style.background=L.surface)} onMouseLeave={e=>(e.currentTarget.style.background=selected.code===c.code?L.surface:"transparent")}>
                <span style={{fontSize:16}}>{c.flag}</span><span style={{fontWeight:600,fontSize:11,width:36}}>{c.code}</span><span style={{fontSize:10,color:L.muted,flex:1,textAlign:"right" as const}}>{c.symbol}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Amount Banner (dark) ── */
function AmountBanner({session,currency}:{session:Session;currency:CurrencyItem}){
  return(
    <div style={{background:D.bg,border:`1px solid ${D.border}`,padding:"16px 20px",marginBottom:24,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
      <span style={{fontSize:11,color:D.muted,letterSpacing:"0.1em"}}>AMOUNT DUE</span>
      <div style={{textAlign:"right" as const}}>
        <div style={{fontSize:22,fontWeight:300,fontFamily:"'Cormorant Garamond',serif",color:D.white}}>{fmtEUR(session.amount_total)}</div>
        {currency.code!=="EUR"&&<div style={{fontSize:10,color:D.muted,marginTop:2}}>≈ {fmtCur(fromEUR(session.amount_total,currency.code),currency)} {currency.code}</div>}
      </div>
    </div>
  );
}

/* ── Section Header ── */
function SectionHeader({icon,title,sub}:{icon:React.ReactNode;title:string;sub:string}){
  return(
    <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:24,paddingBottom:20,borderBottom:`1px solid ${L.border}`}}>
      <div style={{width:44,height:44,background:D.bg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{icon}</div>
      <div><p style={{fontSize:13,fontWeight:600,color:L.text,letterSpacing:"0.06em"}}>{title}</p><p style={{fontSize:10,color:L.muted,marginTop:2}}>{sub}</p></div>
    </div>
  );
}

/* ── Pay Button ── */
function PayBtn({busy,label,onClick,disabled=false}:{busy:boolean;label:React.ReactNode;onClick:()=>void;disabled?:boolean}){
  return(
    <motion.button onClick={onClick} disabled={busy||disabled} whileTap={(!busy&&!disabled)?{scale:0.98} as any:undefined} style={{width:"100%",padding:"15px",background:busy||disabled?L.border:D.bg,border:"none",color:busy||disabled?L.muted:D.white,cursor:busy||disabled?"not-allowed":"pointer",fontSize:11,fontWeight:600,letterSpacing:"0.2em",fontFamily:"'Montserrat',sans-serif",display:"flex",alignItems:"center",justifyContent:"center",gap:10}}>
      {busy?<><span className="co-spin" style={{width:14,height:14,border:"2px solid #ccc",borderTop:"2px solid #888",borderRadius:"50%",display:"inline-block"}}/>PROCESSING…</>:label}
    </motion.button>
  );
}

/* ── Error box ── */
const ErrBox=({msg}:{msg:string})=><div style={{background:"#fff5f5",border:"1px solid #fcc",padding:"10px 14px",fontSize:12,color:"#c00",marginBottom:16}}>{msg}</div>;

/* ── Lock notice ── */
const LockNote=({text}:{text:string})=>(
  <div style={{display:"flex",alignItems:"center",gap:8,padding:"12px 14px",background:"#f0f9f0",border:"1px solid #d4ecd4",marginBottom:20,fontSize:10,color:"#2d7a2d"}}>
    <Lock size={12} color="#2d7a2d"/>{text}
  </div>
);

/* ══════════════ COSPAY ══════════════ */
function CosPay({session,currency}:{session:Session;currency:CurrencyItem}){
  return(
    <motion.div key="cospay" initial={{opacity:0,y:8}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-8}} transition={{duration:0.25}}>
      <div style={{background:L.surface,border:`1px solid ${L.border}`,padding:"28px 24px"}}>
        <SectionHeader icon={<span style={{fontSize:20}}>⚡</span>} title="CosPay" sub="Pay instantly with your Cosmoola wallet balance"/>
        <AmountBanner session={session} currency={currency}/>
        <div style={{display:"flex",alignItems:"center",gap:8,padding:"12px 14px",background:"#fffbf0",border:"1px solid #f0e8c8",marginBottom:24,fontSize:10,color:"#b8860b"}}>
          <AlertCircle size={13} color="#b8860b"/>CosPay is launching soon — wallet payments coming shortly.
        </div>
        {[{icon:"⚡",t:"Instant",d:"No redirects, no card entry. One tap from your wallet."},{icon:"🔒",t:"Zero-fee",d:"No transaction fees when paying with CosPay balance."},{icon:"🌍",t:"Multi-currency",d:"Hold and spend in EUR, USD, KES and more."}].map(f=>(
          <div key={f.t} style={{display:"flex",alignItems:"flex-start",gap:14,padding:"14px 0",borderBottom:`1px solid ${L.border}`}}>
            <span style={{fontSize:18,width:28,textAlign:"center" as const,flexShrink:0}}>{f.icon}</span>
            <div><p style={{fontSize:12,fontWeight:600,color:L.text,marginBottom:2}}>{f.t}</p><p style={{fontSize:11,color:L.muted}}>{f.d}</p></div>
          </div>
        ))}
        <div style={{marginTop:20}}><PayBtn busy={false} disabled label="COSPAY — COMING SOON" onClick={()=>{}}/></div>
      </div>
    </motion.div>
  );
}

/* ══════════════ CARD ══════════════ */
function CardPayment({session,currency,onSuccess,onError}:{session:Session;currency:CurrencyItem;onSuccess:()=>void;onError:(m:string)=>void}){
  const [f,setF]=useState({name:"",number:"",expiry:"",cvc:""});
  const [busy,setBusy]=useState(false);const [err,setErr]=useState("");const [focused,setFocused]=useState("");
  const pay=async()=>{
    setErr("");if(!f.name||!f.number||!f.expiry||!f.cvc){setErr("Please fill all fields.");return;}setBusy(true);
    try{const[expM,expY]=f.expiry.split("/");const data=await apiPost("confirmPaymentIntent",{id:session.payment_intent,client_secret:session.client_secret,payment_method:{card:{number:f.number.replace(/\s/g,""),exp_month:expM?.trim(),exp_year:expY?.trim(),cvc:f.cvc,name:f.name}}});
      if(data.status==="succeeded"){onSuccess();return;}setErr(data.error?.message||"Payment failed.");}
    catch{setErr("Network error. Try again.");}finally{setBusy(false);}
  };
  const inp=(name:string):React.CSSProperties=>({width:"100%",padding:"14px 16px",fontSize:13,border:`1px solid ${focused===name?L.text:L.border}`,background:L.surface2,color:L.text,outline:"none",fontFamily:"'Montserrat',sans-serif",boxSizing:"border-box",transition:"border-color 0.2s"});
  return(
    <motion.div key="card" initial={{opacity:0,y:8}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-8}} transition={{duration:0.25}}>
      <div style={{background:L.surface,border:`1px solid ${L.border}`,padding:"28px 24px"}}>
        <SectionHeader icon={<CreditCard size={20} color={D.gold} strokeWidth={1.5}/>} title="Card Payment" sub="Visa · Mastercard · Amex · All major cards"/>
        <AmountBanner session={session} currency={currency}/>
        {err&&<ErrBox msg={err}/>}
        <div style={{marginBottom:16}}>
          <label style={{display:"block",fontSize:9,fontWeight:700,letterSpacing:"0.2em",color:L.muted,marginBottom:8,textTransform:"uppercase" as const}}>Cardholder Name</label>
          <input style={inp("name")} placeholder="As printed on card" value={f.name} onChange={e=>setF(p=>({...p,name:e.target.value}))} onFocus={()=>setFocused("name")} onBlur={()=>setFocused("")}/>
        </div>
        <div style={{marginBottom:16}}>
          <label style={{display:"block",fontSize:9,fontWeight:700,letterSpacing:"0.2em",color:L.muted,marginBottom:8,textTransform:"uppercase" as const}}>Card Number</label>
          <input style={{...inp("number"),fontFamily:"monospace",letterSpacing:2}} placeholder="4242 4242 4242 4242" value={f.number} inputMode="numeric" onChange={e=>setF(p=>({...p,number:fmtCard(e.target.value)}))} onFocus={()=>setFocused("number")} onBlur={()=>setFocused("")}/>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:20}}>
          <div>
            <label style={{display:"block",fontSize:9,fontWeight:700,letterSpacing:"0.2em",color:L.muted,marginBottom:8,textTransform:"uppercase" as const}}>Expiry</label>
            <input style={{...inp("expiry"),fontFamily:"monospace"}} placeholder="MM/YY" value={f.expiry} inputMode="numeric" onChange={e=>setF(p=>({...p,expiry:fmtExp(e.target.value)}))} onFocus={()=>setFocused("expiry")} onBlur={()=>setFocused("")}/>
          </div>
          <div>
            <label style={{display:"block",fontSize:9,fontWeight:700,letterSpacing:"0.2em",color:L.muted,marginBottom:8,textTransform:"uppercase" as const}}>CVC</label>
            <input style={{...inp("cvc"),fontFamily:"monospace"}} placeholder="123" value={f.cvc} inputMode="numeric" maxLength={4} onChange={e=>setF(p=>({...p,cvc:e.target.value.replace(/\D/g,"").slice(0,4)}))} onFocus={()=>setFocused("cvc")} onBlur={()=>setFocused("")}/>
          </div>
        </div>
        <LockNote text="Card details encrypted end-to-end. Cosmoola never stores raw card data."/>
        <PayBtn busy={busy} label={<><CreditCard size={14}/>PAY {fmtEUR(session.amount_total)}</>} onClick={pay}/>
      </div>
    </motion.div>
  );
}

/* ══════════════ MOBILE MONEY ══════════════ */
function MobileMoney({session,currency,onPending,onError}:{session:Session;currency:CurrencyItem;onPending:(k:MobileKey)=>void;onError:(m:string)=>void}){
  const [active,setActive]=useState<MobileKey>("mpesa");
  const prov=MOBILE_PROVIDERS.find(p=>p.key===active)!;
  const [phone,setPhone]=useState("");const [busy,setBusy]=useState(false);const [err,setErr]=useState("");
  const localAmt=fromEUR(session.amount_total,prov.cur);
  const pay=async()=>{
    setErr("");if(!phone.trim()){setErr("Enter your phone number.");return;}setBusy(true);
    try{const data=await apiPost(prov.ep,{payment_intent_id:session.payment_intent,phone:phone.trim(),amount:Math.round(localAmt),currency:prov.cur});
      if(data.error){setErr(data.error.message||"Request failed.");return;}onPending(active);}
    catch{setErr("Network error. Try again.");}finally{setBusy(false);}
  };
  return(
    <motion.div key="mobile" initial={{opacity:0,y:8}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-8}} transition={{duration:0.25}}>
      <div style={{background:L.surface,border:`1px solid ${L.border}`,padding:"28px 24px"}}>
        <SectionHeader icon={<Smartphone size={20} color={D.gold} strokeWidth={1.5}/>} title="Mobile Money" sub="M-Pesa · MTN MoMo · Airtel · Orange · Instant STK push"/>

        {/* Provider grid */}
        <div style={{marginBottom:20}}>
          <label style={{display:"block",fontSize:9,fontWeight:700,letterSpacing:"0.2em",color:L.muted,marginBottom:10,textTransform:"uppercase" as const}}>Select Provider</label>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
            {MOBILE_PROVIDERS.map(p=>(
              <button key={p.key} onClick={()=>{setActive(p.key);setPhone("");setErr("");}} style={{
                display:"flex",alignItems:"center",gap:10,padding:"14px",
                border:active===p.key?`2px solid ${L.text}`:`1px solid ${L.border}`,
                background:active===p.key?L.text:"transparent",
                color:active===p.key?L.bg:L.text,cursor:"pointer",
                fontFamily:"'Montserrat',sans-serif",transition:"all 0.15s",textAlign:"left" as const,
              }}>
                <span style={{fontSize:22}}>{p.flag}</span>
                <div>
                  <div style={{fontSize:11,fontWeight:600}}>{p.label}</div>
                  <div style={{fontSize:9,opacity:0.6,marginTop:1}}>{p.sym} · {p.cur}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Amount in local + EUR */}
        <div style={{background:D.bg,border:`1px solid ${D.border}`,padding:"16px 20px",marginBottom:20}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div>
              <div style={{fontSize:9,color:D.muted,letterSpacing:"0.15em",marginBottom:4}}>CHARGED IN EUR</div>
              <div style={{fontSize:22,fontWeight:300,fontFamily:"'Cormorant Garamond',serif",color:D.gold}}>{fmtEUR(session.amount_total)}</div>
            </div>
            <div style={{textAlign:"right" as const}}>
              <div style={{fontSize:9,color:D.muted,letterSpacing:"0.15em",marginBottom:4}}>≈ IN {prov.cur}</div>
              <div style={{fontSize:18,fontWeight:300,fontFamily:"'Cormorant Garamond',serif",color:D.white}}>
                {prov.sym}{localAmt.toLocaleString("en",{minimumFractionDigits:0,maximumFractionDigits:0})}
              </div>
            </div>
          </div>
          <div style={{fontSize:9,color:D.muted,marginTop:10,borderTop:`1px solid ${D.border}`,paddingTop:8}}>
            Indicative conversion · Final charge in EUR · Powered by Cosmoola
          </div>
        </div>

        {/* Phone input */}
        <div style={{marginBottom:20}}>
          <label style={{display:"block",fontSize:9,fontWeight:700,letterSpacing:"0.2em",color:L.muted,marginBottom:8,textTransform:"uppercase" as const}}>
            {prov.flag} {prov.label} Number
          </label>
          <input type="tel" placeholder={prov.hint} value={phone} onChange={e=>setPhone(e.target.value)}
            style={{width:"100%",padding:"14px 16px",fontSize:14,border:`1px solid ${L.border}`,background:L.surface2,color:L.text,outline:"none",fontFamily:"monospace",boxSizing:"border-box" as const,letterSpacing:1}}/>
          <p style={{fontSize:10,color:L.muted,marginTop:5}}>Enter local or international format (e.g. 0712 345 678 or +254 712 345 678)</p>
        </div>

        {err&&<ErrBox msg={err}/>}
        <LockNote text={`A prompt will be sent to your phone. Enter your ${prov.label} PIN to confirm. Cosmoola never sees your PIN.`}/>
        <PayBtn busy={busy} label={<><span style={{fontSize:16}}>{prov.flag}</span>PAY WITH {prov.label.toUpperCase()}</>} onClick={pay}/>
      </div>
    </motion.div>
  );
}

/* ══════════════ PENDING ══════════════ */
function PendingScreen({mKey,session,onSuccess,onError}:{mKey:MobileKey;session:Session;onSuccess:()=>void;onError:(m:string)=>void}){
  const prov=MOBILE_PROVIDERS.find(p=>p.key===mKey)!;
  const [dots,setDots]=useState(".");
  const poll=useRef<ReturnType<typeof setInterval>|null>(null);
  useEffect(()=>{
    const di=setInterval(()=>setDots(d=>d.length>=3?".":d+"."),600);
    poll.current=setInterval(async()=>{try{const data=await apiGet("getPaymentStatus",{id:session.payment_intent});if(data.status==="succeeded"){clearInterval(di);if(poll.current)clearInterval(poll.current);onSuccess();}if(data.status==="failed"){clearInterval(di);if(poll.current)clearInterval(poll.current);onError("Payment declined.");}}catch{}},3000);
    return()=>{clearInterval(di);if(poll.current)clearInterval(poll.current);};
  },[]);
  return(
    <motion.div key="pending" initial={{opacity:0,y:8}} animate={{opacity:1,y:0}} style={{background:L.surface,border:`1px solid ${L.border}`,padding:"48px 28px",textAlign:"center" as const}}>
      <div style={{fontSize:52,marginBottom:16}}>{prov.flag}</div>
      <h3 style={{fontSize:20,fontWeight:300,fontFamily:"'Cormorant Garamond',serif",color:L.text,marginBottom:8}}>Check Your Phone{dots}</h3>
      <p style={{fontSize:12,color:L.textSoft,lineHeight:1.8,maxWidth:360,margin:"0 auto 24px"}}>
        A <strong>{prov.label}</strong> prompt was sent. Enter your PIN to complete <strong style={{color:L.text}}>{fmtEUR(session.amount_total)}</strong>.
      </p>
      <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,fontSize:11,color:L.muted}}>
        <span className="co-spin" style={{width:12,height:12,border:"2px solid #ddd",borderTop:"2px solid #999",borderRadius:"50%",display:"inline-block"}}/>
        Checking every 3 seconds…
      </div>
    </motion.div>
  );
}

/* ══════════════ GOOGLE / APPLE ══════════════ */
function DigitalWallet({type,session,currency}:{type:"google"|"apple";session:Session;currency:CurrencyItem}){
  const isApple=type==="apple";
  const features=isApple
    ?[{i:"⚡",t:"One tap",d:"No forms, no redirects. Authenticate and done."},{i:"🔒",t:"Secure Enclave",d:"Biometric auth via Face ID or Touch ID."},{i:"🍎",t:"Native iOS",d:"iPhone, iPad, Safari on Mac."}]
    :[{i:"⚡",t:"Instant",d:"Funds confirmed immediately."},{i:"🔒",t:"Tokenised",d:"Google never shares your real card number."},{i:"📱",t:"Biometric",d:"Confirm with fingerprint or PIN."}];
  return(
    <motion.div key={type} initial={{opacity:0,y:8}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-8}} transition={{duration:0.25}}>
      <div style={{background:L.surface,border:`1px solid ${L.border}`,padding:"28px 24px"}}>
        <SectionHeader
          icon={isApple?<span style={{fontSize:20,color:"#fff"}}>🍎</span>:
            <svg width="20" height="20" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>}
          title={isApple?"Apple Pay":"Google Pay"}
          sub={isApple?"Pay with Face ID, Touch ID, or passcode":"Pay with cards saved in your Google account"}
        />
        <AmountBanner session={session} currency={currency}/>
        {features.map(f=>(
          <div key={f.t} style={{display:"flex",alignItems:"flex-start",gap:14,padding:"14px 0",borderBottom:`1px solid ${L.border}`}}>
            <span style={{fontSize:18,width:28,textAlign:"center" as const,flexShrink:0}}>{f.i}</span>
            <div><p style={{fontSize:12,fontWeight:600,color:L.text,marginBottom:2}}>{f.t}</p><p style={{fontSize:11,color:L.muted}}>{f.d}</p></div>
          </div>
        ))}
        <div style={{marginTop:20,padding:"12px 14px",background:"#fffbf0",border:"1px solid #f0e8c8",display:"flex",alignItems:"center",gap:8,marginBottom:20}}>
          <AlertCircle size={13} color="#b8860b"/>
          <span style={{fontSize:10,color:"#b8860b"}}>{isApple?"Apple Pay":"Google Pay"} is in beta. {isApple?"Available on Safari and iOS only.":"Available on Chrome and Android."}</span>
        </div>
        <button disabled style={{width:"100%",padding:"15px",background:isApple?"#000":L.border,border:"none",color:isApple?"rgba(255,255,255,0.3)":L.muted,cursor:"not-allowed",fontSize:11,fontWeight:600,letterSpacing:"0.2em",fontFamily:"'Montserrat',sans-serif"}}>
          {isApple?"🍎":"G"} PAY — COMING SOON
        </button>
      </div>
    </motion.div>
  );
}

/* ══════════════ MAIN PAGE ══════════════ */
export default function Checkout(){
  const dispatch=useDispatch<AppDispatch>();
  const navigate=useNavigate();
  const {session,loading,error}=useSelector((s:RootState)=>s.payments);
  const sessionId=window.location.pathname.split("/").pop();
  const [tab,setTab]=useState<PayTab>("mobile");
  const [stage,setStage]=useState<Stage>("form");
  const [stgErr,setStgErr]=useState("");
  const [pendingKey,setPendingKey]=useState<MobileKey|null>(null);
  const [loaded,setLoaded]=useState(false);
  const [currency,setCurrency]=useState<CurrencyItem>(ALL_CURRENCIES.find(c=>c.code==="EUR")!);

  useEffect(()=>{const unsub=dispatch(listenToCheckoutSession(sessionId));return()=>{if(typeof unsub==="function")unsub();};},[sessionId]);
  useEffect(()=>{setTimeout(()=>setLoaded(true),80);},[]);
  useEffect(()=>{if(stage==="success"&&session?.success_url){const url=session.success_url.replace("{CHECKOUT_SESSION_ID}",sessionId??"");setTimeout(()=>{window.location.href=url;},2200);}},[stage]);

  const onSuccess=()=>setStage("success");
  const onError=(msg:string)=>{setStgErr(msg);setStage("error");};
  const onPending=(k:MobileKey)=>{setPendingKey(k);setStage("pending");};
  const onCancel=()=>{if(session?.cancel_url)window.location.href=session.cancel_url;};

  const lineItems=session?.line_items?.data??[];
  const productName=lineItems[0]?.price_data?.product_data?.name;

  const TABS:{id:PayTab;label:string;icon:React.ReactNode}[]=[
    {id:"cospay", label:"CosPay",      icon:<span style={{fontSize:13}}>⚡</span>},
    {id:"card",   label:"Card Payment",icon:<CreditCard size={14} strokeWidth={1.5}/>},
    {id:"mobile", label:"Mobile Money",icon:<Smartphone size={14} strokeWidth={1.5}/>},
    {id:"google", label:"Google Pay",  icon:<span style={{fontWeight:900,fontSize:11,color:"#4285F4",fontFamily:"sans-serif"}}>G</span>},
    {id:"apple",  label:"Apple Pay",   icon:<span style={{fontSize:13}}>🍎</span>},
  ];

  /* full-page success */
  if(stage==="success") return(
    <div style={{minHeight:"100vh",background:L.bg,display:"flex",flexDirection:"column",fontFamily:"'Montserrat',sans-serif"}}>
      <style>{CO_CSS}</style>
      <header style={{background:"rgba(0,0,0,0.95)",borderBottom:`1px solid ${D.border}`,height:58,display:"flex",alignItems:"center",justifyContent:"center"}}>
        <span style={{color:D.white,fontWeight:300,letterSpacing:"0.35em",fontFamily:"'Cormorant Garamond',serif",fontSize:15}}>COSMOOLA</span>
      </header>
      <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",padding:40}}>
        <motion.div initial={{opacity:0,scale:0.96}} animate={{opacity:1,scale:1}} style={{textAlign:"center" as const,maxWidth:400}}>
          <div style={{width:72,height:72,background:"#f0faf4",border:"1px solid #c3e6cb",borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:32,margin:"0 auto 24px"}}>✓</div>
          <h2 style={{fontSize:28,fontWeight:300,fontFamily:"'Cormorant Garamond',serif",color:L.text,marginBottom:8}}>Payment Successful</h2>
          <p style={{fontSize:13,color:L.muted}}>{fmtEUR(session?.amount_total??0)} paid{productName?` for ${productName}`:""} · Redirecting…</p>
        </motion.div>
      </div>
    </div>
  );

  /* full-page error */
  if(stage==="error") return(
    <div style={{minHeight:"100vh",background:L.bg,display:"flex",flexDirection:"column",fontFamily:"'Montserrat',sans-serif"}}>
      <style>{CO_CSS}</style>
      <header style={{background:"rgba(0,0,0,0.95)",borderBottom:`1px solid ${D.border}`,height:58,display:"flex",alignItems:"center",justifyContent:"center"}}>
        <span style={{color:D.white,fontWeight:300,letterSpacing:"0.35em",fontFamily:"'Cormorant Garamond',serif",fontSize:15}}>COSMOOLA</span>
      </header>
      <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",padding:40}}>
        <motion.div initial={{opacity:0,y:12}} animate={{opacity:1,y:0}} style={{textAlign:"center" as const,maxWidth:400}}>
          <div style={{width:72,height:72,background:"#fff5f5",border:"1px solid #fcc",borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:32,margin:"0 auto 24px"}}>✕</div>
          <h2 style={{fontSize:28,fontWeight:300,fontFamily:"'Cormorant Garamond',serif",color:L.text,marginBottom:8}}>Payment Failed</h2>
          <p style={{fontSize:13,color:L.muted,marginBottom:24}}>{stgErr}</p>
          <button onClick={()=>{setStage("form");setStgErr("");}} style={{padding:"12px 32px",background:D.bg,color:D.white,border:"none",cursor:"pointer",fontSize:11,fontWeight:600,letterSpacing:"0.15em",fontFamily:"'Montserrat',sans-serif",display:"inline-flex",alignItems:"center",gap:8}}>
            <RefreshCw size={13}/>TRY AGAIN
          </button>
        </motion.div>
      </div>
    </div>
  );

  return(
    <div style={{minHeight:"100vh",background:L.bg,color:L.text,fontFamily:"'Montserrat','Cormorant Garamond',system-ui,sans-serif"}}>
      <style>{CO_CSS}</style>

      {/* ── HEADER — copy-paste of PaymentMethods ── */}
      <header style={{position:"sticky",top:0,zIndex:100,background:"rgba(0,0,0,0.95)",backdropFilter:"blur(12px)",borderBottom:`1px solid ${D.border}`,display:"flex",alignItems:"center",justifyContent:"space-between",padding:"0 20px",height:58}}>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <button onClick={()=>navigate(-1)} style={{background:"none",border:"none",cursor:"pointer",color:D.offwhite,display:"flex",alignItems:"center",gap:6,fontSize:11,fontFamily:"'Montserrat',sans-serif",letterSpacing:"0.05em"}}>
            <ArrowLeft size={16} strokeWidth={1.5}/><span className="co-back-label">BACK</span>
          </button>
        </div>
        <div style={{position:"absolute",left:"50%",transform:"translateX(-50%)"}}>
          <span style={{color:D.white,fontWeight:300,letterSpacing:"0.35em",fontFamily:"'Cormorant Garamond',serif",fontSize:15}}>COSMOOLA</span>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <CurrencyPicker selected={currency} onSelect={setCurrency}/>
          <img src={logo} alt="C" style={{width:28,height:28,borderRadius:0,objectFit:"cover"}}/>
        </div>
      </header>

      <div className="co-container">
        <div className="co-main">

          {/* ── TITLE ── */}
          <motion.div initial={{opacity:0,y:16}} animate={{opacity:loaded?1:0,y:loaded?0:16}} transition={{duration:0.4}} style={{marginBottom:28}}>
            <h1 style={{fontSize:28,fontWeight:300,color:L.text,fontFamily:"'Cormorant Garamond',serif",letterSpacing:"0.06em",margin:0}}>Select Payment Method</h1>
            <p style={{fontSize:12,color:L.textSoft,marginTop:6,letterSpacing:"0.05em"}}>
              {productName?`Paying for: ${productName} · `:""}{loading?"Loading…":session?`${fmtEUR(session.amount_total)} due`:""}
            </p>
          </motion.div>

          {/* ── LOADING ── */}
          {loading&&(
            <div style={{background:L.surface,border:`1px solid ${L.border}`,padding:"60px",textAlign:"center" as const}}>
              <div className="co-spin" style={{width:32,height:32,border:`2px solid ${L.border}`,borderTop:`2px solid ${L.text}`,borderRadius:"50%",margin:"0 auto 16px"}}/>
              <div style={{fontSize:12,color:L.muted,letterSpacing:"0.1em"}}>LOADING CHECKOUT…</div>
            </div>
          )}

          {/* ── SESSION ERROR ── */}
          {error&&!loading&&(
            <div style={{background:L.surface,border:`1px solid ${L.border}`,padding:"48px 28px",textAlign:"center" as const}}>
              <div style={{fontSize:40,marginBottom:16}}>⚠️</div>
              <div style={{fontSize:16,fontWeight:400,fontFamily:"'Cormorant Garamond',serif",color:L.text,marginBottom:8}}>{error}</div>
              <div style={{fontSize:12,color:L.muted}}>Return to the merchant and try again.</div>
            </div>
          )}

          {/* ── TABS + CONTENT ── */}
          {!loading&&!error&&session&&(
            <>
              <motion.div initial={{opacity:0,y:16}} animate={{opacity:loaded?1:0,y:loaded?0:16}} transition={{duration:0.4,delay:0.05}}
                style={{display:"flex",gap:0,borderBottom:`1px solid ${L.border}`,marginBottom:24,overflowX:"auto"}}>
                {TABS.map(t=>(
                  <button key={t.id} onClick={()=>setTab(t.id)} style={{
                    display:"flex",alignItems:"center",gap:8,padding:"14px 16px",
                    background:"transparent",border:"none",
                    borderBottom:tab===t.id?`2px solid ${L.text}`:"2px solid transparent",
                    color:tab===t.id?L.text:L.muted,cursor:"pointer",
                    fontSize:10,fontWeight:600,letterSpacing:"0.13em",
                    fontFamily:"'Montserrat',sans-serif",whiteSpace:"nowrap" as const,
                    transition:"all 0.2s",marginBottom:-1,
                  }}>
                    {t.icon}{t.label.toUpperCase()}
                  </button>
                ))}
              </motion.div>

              <AnimatePresence mode="wait">
                {tab==="cospay"&&<CosPay session={session} currency={currency}/>}
                {tab==="card"&&stage==="form"&&<CardPayment session={session} currency={currency} onSuccess={onSuccess} onError={onError}/>}
                {tab==="mobile"&&stage==="form"&&<MobileMoney session={session} currency={currency} onPending={onPending} onError={onError}/>}
                {tab==="google"&&<DigitalWallet type="google" session={session} currency={currency}/>}
                {tab==="apple"&&<DigitalWallet type="apple" session={session} currency={currency}/>}
                {stage==="pending"&&pendingKey&&<PendingScreen mKey={pendingKey} session={session} onSuccess={onSuccess} onError={onError}/>}
              </AnimatePresence>

              {(stage==="form"||stage==="pending")&&session?.cancel_url&&(
                <div style={{marginTop:12}}>
                  <button onClick={onCancel} style={{width:"100%",padding:"12px",background:"none",color:L.muted,border:`1px solid ${L.border}`,cursor:"pointer",fontSize:11,fontFamily:"'Montserrat',sans-serif",letterSpacing:"0.05em"}}>
                    Cancel and return to merchant
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* ── SIDEBAR — exact PaymentMethods ── */}
        <div className="co-sidebar">
          <motion.div initial={{opacity:0,y:16}} animate={{opacity:loaded?1:0,y:loaded?0:16}} transition={{duration:0.4,delay:0.2}} style={{position:"sticky",top:80}}>
            {session&&(
              <div style={{background:L.surface,border:`1px solid ${L.border}`,padding:"24px 20px",marginBottom:16}}>
                <p style={{fontSize:10,color:L.textSoft,letterSpacing:"0.2em",marginBottom:16}}>ORDER SUMMARY</p>
                <div style={{display:"flex",flexDirection:"column",gap:10}}>
                  {lineItems.map((li,i)=>(
                    <div key={i} style={{display:"flex",justifyContent:"space-between",fontSize:12}}>
                      <span style={{color:L.textSoft}}>{li.price_data?.product_data?.name||"Item"}{(li.quantity??1)>1?` ×${li.quantity}`:""}</span>
                      <span style={{fontWeight:600,color:L.text}}>{fmtEUR((li.price_data?.unit_amount??0)/100*(li.quantity||1))}</span>
                    </div>
                  ))}
                  <div style={{height:1,background:L.border,margin:"4px 0"}}/>
                  <div style={{display:"flex",justifyContent:"space-between"}}>
                    <span style={{fontSize:11,color:L.muted}}>Total</span>
                    <span style={{fontSize:18,fontWeight:300,fontFamily:"'Cormorant Garamond',serif",color:L.text}}>{fmtEUR(session.amount_total)}</span>
                  </div>
                  {currency.code!=="EUR"&&(
                    <div style={{display:"flex",justifyContent:"space-between"}}>
                      <span style={{fontSize:11,color:L.muted}}>≈ {currency.code}</span>
                      <span style={{fontSize:12,color:L.muted}}>{fmtCur(fromEUR(session.amount_total,currency.code),currency)}</span>
                    </div>
                  )}
                </div>
                <div style={{height:1,background:L.border,margin:"16px 0"}}/>
                <div style={{fontSize:10,color:L.muted,lineHeight:1.7}}>
                  Charged in <strong style={{color:L.text}}>EUR</strong>. Currency shown is indicative.
                </div>
              </div>
            )}
            <div style={{background:D.bg,border:`1px solid ${D.border}`,padding:"20px 18px"}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14}}>
                <Shield size={14} color={D.gold}/>
                <span style={{fontSize:10,color:D.gold,letterSpacing:"0.15em",fontWeight:600}}>SECURITY</span>
              </div>
              {["256-bit TLS encryption","PCI DSS Level 1 compliant","3D Secure authentication","Real-time fraud monitoring","Cosmoola Buyer Protection"].map(item=>(
                <div key={item} style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                  <div style={{width:4,height:4,borderRadius:"50%",background:D.gold,flexShrink:0}}/>
                  <span style={{fontSize:10,color:D.muted}}>{item}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* ── FOOTER — exact PaymentMethods ── */}
      <footer style={{background:D.bg,borderTop:`1px solid ${D.border}`,padding:"28px 24px",marginTop:40}}>
        <div style={{maxWidth:1200,margin:"0 auto",display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap" as const,gap:12}}>
          <div style={{fontSize:11,color:D.muted}}>© 2025 – {new Date().getFullYear()} Cosmoola Technologies · Secure payments</div>
          <div style={{display:"flex",gap:20}}>
            {["privacy","terms","support"].map(p=>(
              <span key={p} onClick={()=>navigate(`/${p}`)} style={{fontSize:11,color:D.muted,cursor:"pointer",textDecoration:"underline",textUnderlineOffset:3}}>
                {p.charAt(0).toUpperCase()+p.slice(1)}
              </span>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}

const CO_CSS=`
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600;700&family=Montserrat:wght@300;400;500;600;700&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  body{background:#fff}button{font-family:inherit}input{font-family:inherit}
  ::-webkit-scrollbar{width:0;height:0}
  input[type=number]::-webkit-inner-spin-button,input[type=number]::-webkit-outer-spin-button{-webkit-appearance:none}
  input[type=number]{-moz-appearance:textfield}
  @keyframes co-spin-anim{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
  .co-spin{animation:co-spin-anim 1s linear infinite}
  .co-container{max-width:1200px;margin:0 auto;padding:32px 24px 0;display:grid;grid-template-columns:1fr 320px;gap:28px;align-items:start}
  .co-main{min-width:0}.co-sidebar{min-width:0}
  @media(max-width:900px){
    .co-container{grid-template-columns:1fr!important;gap:0!important;padding:20px 16px 0!important}
    .co-sidebar{display:none!important}.co-back-label{display:none}
  }
  @media(min-width:901px){.co-back-label{display:inline}}
  input:focus{border-color:#0a0a0a!important}
`;